import { useCallback, useEffect, useState } from "react";
import Cookies from "js-cookie";
import { IoMdClose } from "react-icons/io";
import { SunIcon, MoonIcon, ComputerDesktopIcon } from "@heroicons/react/24/outline";

interface Props {
  setIsOpen: (open: boolean) => void;
}

const modes = [
  { value: "light", label: "Light", Icon: SunIcon },
  { value: "dark", label: "Dark", Icon: MoonIcon },
  { value: "system", label: "System", Icon: ComputerDesktopIcon },
];

const SidePanelContent = ({ setIsOpen }: Props) => {
  const [isMode, setIsMode] = useState(Cookies.get("theme") || "system");

  const applyTheme = useCallback((mode: string) => {
    const root = document.documentElement;
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const isDark =
      mode === "dark" || (mode === "system" && mediaQuery.matches);

    if (isDark) {
      root.classList.add("dark");
      root.classList.remove("light");
      document.body.classList.add("dark");
      document.body.classList.remove("light");
    } else {
      root.classList.remove("dark");
      root.classList.add("light");
      document.body.classList.remove("dark");
      document.body.classList.add("light");
    }

    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", isDark ? "#0F172A" : "#FFFFFF");
    }
  }, []);

  useEffect(() => {
    applyTheme(isMode);

    if (isMode === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = () => applyTheme("system");
      mediaQuery.addEventListener("change", handler);
      return () => mediaQuery.removeEventListener("change", handler);
    }
  }, [isMode, applyTheme]);

  const handleChange = (mode: string) => {
    Cookies.set("theme", mode);
    setIsMode(mode);
  };

  return (
    <div className="h-full flex flex-col bg-white dark:bg-slate-900">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
        <div>
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Appearance
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Choose your preferred theme
          </p>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 transition-colors"
        >
          <IoMdClose className="w-5 h-5" />
        </button>
      </div>

      {/* Theme options */}
      <div className="flex-1 px-5 py-5">
        <div className="space-y-2">
          {modes.map(({ value, label, Icon }) => {
            const isActive = isMode === value;
            return (
              <button
                key={value}
                onClick={() => handleChange(value)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  isActive
                    ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800"
                    : "hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 border border-transparent"
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-sm font-medium">{label}</span>
                {isActive && (
                  <div className="ml-auto w-2 h-2 rounded-full bg-primary-500 dark:bg-primary-400" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SidePanelContent;
